<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useRouter, useRoute } from 'vue-router';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CrmDealsAPI from 'dashboard/api/crmDeals';
import CrmEventsAPI from 'dashboard/api/crmEvents';
import DealModal from '../../crm/shared/DealModal.vue';
import EventModal from '../../crm/calendar/EventModal.vue';
import { useCrmContext } from '../../crm/composables/useCrmContext';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: null,
  },
  isAccordionOpen: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const { crmInboxes: accessibleInboxes, crmMode } = useCrmContext();
const { currentAccount } = useAccount();
const crmHabilitado = computed(() => currentAccount.value?.crm_enabled ?? false);

const allStages = computed(() => store.getters['crmStages/getActiveStages']);
const accessibleInboxIds = computed(
  () => new Set(accessibleInboxes.value.map(inbox => inbox.id))
);
const stages = computed(() => {
  if (crmMode.value !== 'per_inbox') return allStages.value;
  return allStages.value.filter(stage => accessibleInboxIds.value.has(stage.inbox_id));
});
const loading = ref(false);
const eventsLoading = ref(false);
const contactDeals = ref([]);
const contactEvents = ref([]);
const fetched = ref(false);
const showDealModal = ref(false);
const showEventModal = ref(false);

const contactGetter = useMapGetter('contacts/getContact');
const contactDetails = computed(() => contactGetter.value(props.contactId));

const dealSections = computed(() => {
  if (crmMode.value !== 'per_inbox') {
    return contactDeals.value.length
      ? [
          {
            key: 'general',
            label: 'CRM',
            deals: contactDeals.value,
            stages: stages.value,
          },
        ]
      : [];
  }

  return accessibleInboxes.value
    .map(inbox => {
      const inboxStages = stages.value.filter(stage => stage.inbox_id === inbox.id);
      const stageIds = new Set(inboxStages.map(stage => stage.id));
      const deals = contactDeals.value.filter(deal => stageIds.has(deal.crm_stage_id));

      return {
        key: `inbox-${inbox.id}`,
        label: inbox.name,
        inboxId: inbox.id,
        deals,
        stages: inboxStages,
      };
    })
    .filter(section => section.deals.length > 0);
});

async function fetchContactDeals() {
  if (!props.contactId) return;
  loading.value = true;
  try {
    const { data } = await CrmDealsAPI.getByContact(props.contactId, {});
    contactDeals.value = data.payload || [];
    fetched.value = true;
  } catch {
    contactDeals.value = [];
    fetched.value = true;
  } finally {
    loading.value = false;
  }
}

async function fetchContactEvents() {
  if (!props.contactId) return;
  eventsLoading.value = true;
  try {
    const params = {};
    if (props.inboxId) params.inbox_id = props.inboxId;
    const { data } = await CrmEventsAPI.getByContact(props.contactId, params);
    const events = (data.payload || []).filter(
      e => e.status === 'scheduled'
    );
    contactEvents.value = [...events].sort(
      (a, b) => new Date(a.starts_at) - new Date(b.starts_at)
    );
  } catch {
    contactEvents.value = [];
  } finally {
    eventsLoading.value = false;
  }
}

function getStageForDeal(deal) {
  return stages.value.find(stage => stage.id === deal.crm_stage_id) || null;
}

async function changeStage(dealId, stageId) {
  if (!dealId) return;
  try {
    await CrmDealsAPI.move(dealId, stageId);
    await fetchContactDeals();
  } catch {
    // handle error
  }
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
}

function openNewDealModal() {
  showDealModal.value = true;
}

function openDealInKanban(dealId) {
  if (!dealId) return;

  router.push({
    name: 'crm_kanban',
    params: { accountId: route.params.accountId },
    query: { deal_id: String(dealId) },
  });
}

function openEventInCalendar(eventId) {
  if (!eventId) return;

  router.push({
    name: 'crm_calendar',
    params: { accountId: route.params.accountId },
    query: { event_id: String(eventId) },
  });
}

function formatEventDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function closeDealModal() {
  showDealModal.value = false;
}

function openNewEventModal() {
  showEventModal.value = true;
}

function closeEventModal() {
  showEventModal.value = false;
}

async function onDealSaved() {
  showDealModal.value = false;
  await fetchContactDeals();
  await fetchContactEvents();
}

async function onEventSaved() {
  showEventModal.value = false;
  await fetchContactEvents();
}

function loadCrmData() {
  fetchContactDeals();
  fetchContactEvents();
}

watch(
  () => props.contactId,
  (newId, oldId) => {
    if (!crmHabilitado.value) return;
    if (newId && newId !== oldId) {
      fetched.value = false;
      contactDeals.value = [];
      contactEvents.value = [];
      if (props.isAccordionOpen) fetchContactDeals();
      if (props.isAccordionOpen) fetchContactEvents();
    }
  }
);

watch(
  () => props.isAccordionOpen,
  isOpen => {
    if (!crmHabilitado.value) return;
    if (isOpen && !fetched.value) {
      store.dispatch('inboxes/get');
      store.dispatch('crmStages/fetch');
      fetchContactDeals();
      fetchContactEvents();
    }
  }
);

watch(
  () => props.inboxId,
  () => {
    if (!crmHabilitado.value) return;
    if (!props.isAccordionOpen) return;

    store.dispatch('inboxes/get');
    store.dispatch('crmStages/fetch');
    fetchContactDeals();
    fetchContactEvents();
  }
);

onMounted(() => {
  if (!crmHabilitado.value) return;
  if (props.isAccordionOpen) {
    store.dispatch('inboxes/get');
    store.dispatch('crmStages/fetch');
    fetchContactDeals();
    fetchContactEvents();
  }
});
</script>

<template>
  <div class="p-2">
    <!-- Estado: CRM desabilitado nas configurações -->
    <div
      v-if="!crmHabilitado"
      class="rounded-lg bg-n-slate-2 p-3 mb-2"
    >
      <p class="m-0 text-xs text-n-slate-10 text-center">
        CRM não habilitado nas configurações.
      </p>
    </div>

    <!-- Loading -->
    <div
      v-else-if="loading"
      class="flex items-center justify-center gap-2 py-4 text-n-slate-11"
    >
      <Spinner :size="18" />
      <span class="text-xs">Carregando...</span>
    </div>

    <!-- Has deals -->
    <div v-else-if="dealSections.length > 0" class="space-y-3">
      <div
        v-for="section in dealSections"
        :key="section.key"
        class="rounded-lg border border-n-weak overflow-hidden"
        :data-testid="`deal-section-${section.inboxId || section.key}`"
      >
        <div
          v-if="crmMode === 'per_inbox'"
          class="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider bg-n-slate-2 text-n-slate-11"
        >
          {{ section.label }}
        </div>

        <div class="divide-y divide-n-weak">
          <div
            v-for="deal in section.deals"
            :key="deal.id"
            class="p-2 space-y-2"
          >
            <div
              class="rounded-lg border border-n-weak overflow-hidden cursor-pointer hover:border-n-blue-6"
              role="button"
              tabindex="0"
              :data-testid="`deal-card-${deal.id}`"
              @click="openDealInKanban(deal.id)"
              @keydown.enter.prevent="openDealInKanban(deal.id)"
              @keydown.space.prevent="openDealInKanban(deal.id)"
            >
              <div class="flex items-center justify-between px-3 py-2 text-xs bg-n-slate-2">
                <span class="font-medium text-n-slate-12">Card</span>
                <span class="text-n-blue-11 truncate ml-2 max-w-[70%]">
                  {{ deal.title }}
                </span>
              </div>
              <div v-if="deal.value" class="flex items-center justify-between px-3 py-2 text-xs">
                <span class="font-medium text-n-slate-12">Valor</span>
                <span class="text-n-teal-11 font-medium">{{ formatCurrency(deal.value) }}</span>
              </div>
              <div class="flex items-center justify-between px-3 py-2 text-xs bg-n-slate-2">
                <span class="font-medium text-n-slate-12">Estágio Atual</span>
                <span v-if="getStageForDeal(deal)" class="inline-flex items-center gap-1.5 text-n-slate-11">
                  <span class="w-2 h-2 rounded-full" :style="{ backgroundColor: getStageForDeal(deal).color }" />
                  {{ getStageForDeal(deal).name }}
                </span>
                <span v-else class="text-n-slate-10">Não definido</span>
              </div>
            </div>

            <div>
              <label class="block text-[10px] font-medium text-n-slate-10 mb-1 uppercase tracking-wider">
                Mover para estágio
              </label>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="stage in section.stages"
                  :key="stage.id"
                  class="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors border"
                  :class="
                    getStageForDeal(deal) && getStageForDeal(deal).id === stage.id
                      ? 'border-n-blue-7 bg-n-blue-3 text-n-blue-11'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-slate-2'
                  "
                  @click="changeStage(deal.id, stage.id)"
                >
                  <span class="w-1.5 h-1.5 rounded-full" :style="{ backgroundColor: stage.color }" />
                  {{ stage.name }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <NextButton
        label="Novo Card"
        icon="i-lucide-plus"
        blue
        faded
        xs
        class="w-full"
        @click="openNewDealModal"
      />
    </div>

    <!-- No deals -->
    <div v-else-if="fetched" class="space-y-2">
      <div class="rounded-lg bg-n-slate-2 p-3">
        <p class="m-0 text-xs text-n-slate-11 text-center">
          Nenhum card ativo para este contato.
        </p>
      </div>

      <!-- Create deal button -->
      <NextButton
        label="Criar Card"
        icon="i-lucide-plus"
        blue
        faded
        xs
        class="w-full"
        @click="openNewDealModal"
      />
    </div>

    <!-- Not fetched -->
    <div v-else class="flex flex-col items-center gap-2 py-3">
      <p class="m-0 text-xs text-n-slate-10 text-center">
        Veja informações do CRM para este contato.
      </p>
      <NextButton
        label="Carregar CRM"
        icon="i-lucide-refresh-cw"
        slate
        faded
        sm
        @click="loadCrmData"
      />
    </div>

    <!-- Contact events -->
    <div
      v-if="fetched"
      class="rounded-lg border border-n-weak overflow-hidden mt-2"
    >
      <div class="flex items-center justify-between px-3 py-2 text-xs bg-n-slate-2">
        <span class="font-medium text-n-slate-12">Agenda</span>
        <div class="flex items-center gap-2">
          <span class="text-n-slate-10">{{ contactEvents.length }} evento(s)</span>
          <NextButton
            label="Novo Evento"
            icon="i-lucide-plus"
            blue
            faded
            xs
            @click="openNewEventModal"
          />
        </div>
      </div>

      <div v-if="eventsLoading" class="px-3 py-2 text-xs text-n-slate-10">
        Carregando agenda...
      </div>

      <div v-else-if="contactEvents.length === 0" class="px-3 py-2 text-xs text-n-slate-10">
        Nenhum evento associado a este contato.
      </div>

      <template v-else>
        <button
          v-for="event in contactEvents.slice(0, 3)"
          :key="event.id"
          type="button"
          class="w-full flex items-center justify-between gap-3 px-3 py-2 text-xs border-t border-n-weak hover:bg-n-slate-2"
          @click="openEventInCalendar(event.id)"
        >
          <span class="text-n-slate-12 truncate text-left">{{ event.title }}</span>
          <span class="text-n-slate-10 whitespace-nowrap">{{ formatEventDate(event.starts_at) }}</span>
        </button>
      </template>
    </div>

    <!-- Deal Modal -->
    <DealModal
      v-if="showDealModal"
      :stages="stages"
      :inboxes="crmMode === 'per_inbox' ? accessibleInboxes : []"
      :initial-contact-id="Number(contactId)"
      :initial-contact="contactDetails"
      :initial-inbox-id="inboxId"
      @close="closeDealModal"
      @saved="onDealSaved"
    />

    <!-- Event Modal -->
    <EventModal
      v-if="showEventModal"
      :initial-contact-id="Number(contactId)"
      :initial-contact="contactDetails"
      @close="closeEventModal"
      @saved="onEventSaved"
    />
  </div>
</template>
